import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Modal } from './Modal';
import { Button } from './Button';
import { ReleaseNoteEntry } from './ReleaseNoteEntry';
import { useVisibleReleaseNotes } from '../../hooks/useVisibleReleaseNotes';
import { formatDate } from '../../utils/dateUtils';

/**
 * Zeigt der Verwaltung den neuesten Release-Eintrag einmal pro Version.
 *
 * Neben ToastContainer in MainLayout und AdminShell gerendert — damit erscheint der
 * Dialog nur auf authentifizierten Routen.
 */
export function WhatsNewModal() {
  const { t } = useTranslation('releaseNotes');
  const navigate = useNavigate();
  const { notes, hasUnseen, lastSeenVersion, markAllSeen } = useVisibleReleaseNotes();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (lastSeenVersion === null) {
      // Erster Besuch in diesem Browser: still auf Stand setzen. Neue Nutzer sollen
      // nicht mit der Änderungshistorie begrüßt werden.
      markAllSeen();
      return;
    }
    if (hasUnseen) {
      setIsOpen(true);
    }
  }, [lastSeenVersion, hasUnseen, markAllSeen]);

  const latest = notes[0];
  if (!latest) return null;

  const close = () => {
    markAllSeen();
    setIsOpen(false);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      title={t('modal.title')}
      size="lg"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => {
              close();
              navigate('/release-notes');
            }}
          >
            {t('modal.showAll')}
          </Button>
          <Button onClick={close}>{t('modal.confirm')}</Button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <p className="text-[15px] font-semibold text-slate-900 tracking-tight">
            {latest.title}
          </p>
          <p className="text-[11px] font-mono tabular-nums text-slate-500 mt-0.5">
            {t('meta', { version: latest.version, date: formatDate(latest.date) })}
          </p>
        </div>
        <ReleaseNoteEntry note={latest} hideMeta />
      </div>
    </Modal>
  );
}

export default WhatsNewModal;
